// 알림 설정 화면
import React, { useState, useEffect, useCallback } from 'react';
import PageLayout from '../../components/PageLayout';
import AlertTypePage from './AlertTypePage';
import {
  fetchTelegramSettings, saveTelegramSettings, fetchChatIdUrl, sendTelegramTest,
  fetchAlertConfigs, createAlertConfig, updateAlertConfig, deleteAlertConfig, toggleAlertConfig,
  fetchAlertHistory, runAlertCheckNow,
} from '../../api/alert';
import './AlertPage.css';

const COOLDOWN_OPTIONS = [
  { value: 'ONCE',       label: '1회만' },
  { value: 'DAILY_TIME', label: '다음날 지정시간' },
];

const MINUTE_OPTIONS = [0, 10, 20, 30, 40, 50];

const INDEX_OPTIONS = [
  { group: '국내지수',  items: [
    { value: 'KOSPI',    label: '코스피' },
    { value: 'KOSDAQ',   label: '코스닥' },
    { value: 'KOSPI200', label: '코스피200' },
    { value: 'VKOSPI',   label: 'VKOSPI (한국 공포지수)' },
  ]},
  { group: '미국지수',  items: [
    { value: 'SP500',    label: 'S&P500' },
    { value: 'NASDAQ',   label: '나스닥100' },
    { value: 'VIX',      label: 'VIX 공포지수' },
  ]},
  { group: '환율',      items: [
    { value: 'USD_KRW',  label: '원달러 환율' },
    { value: 'DXY',      label: '달러인덱스' },
    { value: 'JPY_KRW',  label: '원엔 환율' },
  ]},
  { group: '원자재',    items: [
    { value: 'GOLD',     label: '금' },
    { value: 'SILVER',   label: '은' },
    { value: 'BRENT',    label: '브렌트유' },
    { value: 'WTI',      label: 'WTI유' },
  ]},
];
const ALL_INDEX_ITEMS = INDEX_OPTIONS.flatMap(g => g.items);

const METRIC_LABELS = { PROFIT_RATE: '수익률', PRICE: '시세', DIFF_RATE: '당일등락률' };
const SOURCE_LABELS  = { BUY: '매수', SELL: '매도', MARKET: '시장' };

const EMPTY_MARKET_FORM = {
  id:              null,
  alert_name:      '',
  index_code:      'USD_KRW',
  condition:       'BOTH',
  threshold_upper: '',
  threshold_lower: '',
  cooldown_type:   'DAILY_TIME',
  reset_hour:      9,
  reset_minute:    0,
  is_active:       true,
};

const cdLabel = (v, hour, minute) => {
  if (v === 'ONCE') return '1회만';
  if (v === 'DAILY_TIME')
    return `다음날 ${String(hour ?? 9).padStart(2, '0')}:${String(minute ?? 0).padStart(2, '0')}`;
  return v || '-';
};

const fDt = (dt) => {
  if (!dt) return '-';
  return dt.replace('T', ' ').substring(0, 16);
};

const fVal = (val, metric) => {
  if (val == null) return '-';
  const v = parseFloat(val);
  if (metric === 'PROFIT_RATE' || metric === 'DIFF_RATE') return `${v.toFixed(2)}%`;
  return v.toLocaleString();
};

const condLabel = (c) => {
  if (c === 'GTE') return '이상(>=)';
  if (c === 'LTE') return '이하(<=)';
  if (c === 'BOTH') return '양방향';
  return c || '-';
};

const AlertPage = ({ isModal = false, initialTab = 'type', onClose }) => {
  const [tab, setTab] = useState(initialTab);

  // ── 텔레그램 설정 ─────────────────────────────────────────
  const [tgSettings, setTgSettings] = useState({ telegram_chat_id: '', telegram_enabled: false });
  const [tgSaving,   setTgSaving]   = useState(false);
  const [tgMsg,      setTgMsg]      = useState('');
  const [testBusy,   setTestBusy]   = useState(false);

  // ── 시장지수 알림 ─────────────────────────────────────────
  const [configs,    setConfigs]    = useState([]);
  const [loading,    setLoading]    = useState(false);
  const [message,    setMessage]    = useState({ text: '', type: '' });
  const [mktPopup,   setMktPopup]   = useState(null);
  const [mktForm,    setMktForm]    = useState(EMPTY_MARKET_FORM);
  const [mktBusy,    setMktBusy]    = useState(false);

  // ── 발송 이력 ─────────────────────────────────────────────
  const [history,     setHistory]     = useState({ data: [], total: 0, page: 1 });
  const [histLoading, setHistLoading] = useState(false);
  const [msgDetail,   setMsgDetail]   = useState(null);

  // ── 수동 알림체크 ─────────────────────────────────────────
  const [checkBusy,   setCheckBusy]   = useState(false);

  const showMsg = (text, type = 'success') => {
    setMessage({ text, type });
    setTimeout(() => setMessage({ text: '', type: '' }), 3000);
  };

  useEffect(() => {
    fetchTelegramSettings()
      .then(d => setTgSettings({ telegram_chat_id: d.telegram_chat_id || '', telegram_enabled: !!d.telegram_enabled }))
      .catch(() => {});
    loadConfigs();
  }, []); // eslint-disable-line

  const loadConfigs = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetchAlertConfigs();
      setConfigs(res.data || []);
    } catch { showMsg('알림 설정 조회 실패', 'error'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => {
    if (tab === 'history') loadHistory(1);
  }, [tab]); // eslint-disable-line

  const loadHistory = async (page) => {
    setHistLoading(true);
    try {
      const res = await fetchAlertHistory(page);
      setHistory({ data: res.data || [], total: res.total || 0, page });
    } catch { /* silent */ }
    finally { setHistLoading(false); }
  };

  // ── 텔레그램 ─────────────────────────────────────────────
  const handleTgSave = async () => {
    setTgSaving(true); setTgMsg('');
    try {
      await saveTelegramSettings({ telegram_chat_id: tgSettings.telegram_chat_id, telegram_enabled: tgSettings.telegram_enabled });
      setTgMsg('✅ 저장되었습니다.');
    } catch { setTgMsg('❌ 저장에 실패했습니다.'); }
    finally { setTgSaving(false); }
  };

  const handleTest = async () => {
    if (!tgSettings.telegram_chat_id) { setTgMsg('❌ Chat ID를 먼저 입력하세요.'); return; }
    setTestBusy(true); setTgMsg('');
    try {
      await sendTelegramTest(tgSettings.telegram_chat_id);
      setTgMsg('✅ 테스트 메시지를 전송했습니다.');
    } catch { setTgMsg('❌ 전송 실패. Chat ID를 확인해 주세요.'); }
    finally { setTestBusy(false); }
  };

  const handleChatIdUrl = async () => {
    try {
      const url = await fetchChatIdUrl();
      window.open(url, '_blank');
    } catch { alert('URL 조회 실패'); }
  };

  const handleCheckNow = async () => {
    setCheckBusy(true);
    try {
      const res = await runAlertCheckNow();
      if (res.success) showMsg('알림체크 완료 — 텔레그램을 확인해 주세요.', 'success');
      else             showMsg(`알림체크 오류: ${res.message}`, 'error');
    } catch (e) {
      showMsg(`알림체크 실패: ${e.message}`, 'error');
    } finally {
      setCheckBusy(false);
    }
  };

  // ── 시장 알림 팝업 ────────────────────────────────────────
  const openMktNew = () => { setMktForm(EMPTY_MARKET_FORM); setMktPopup('new'); };
  const openMktEdit = (row) => {
    setMktForm({
      id:              row.id,
      alert_name:      row.alert_name,
      index_code:      row.index_code || 'USD_KRW',
      condition:       row.condition,
      threshold_upper: row.threshold_upper ?? '',
      threshold_lower: row.threshold_lower ?? '',
      cooldown_type:   row.cooldown_type   || 'DAILY_TIME',
      reset_hour:      row.reset_hour      ?? 9,
      reset_minute:    row.reset_minute    ?? 0,
      is_active:       row.is_active,
    });
    setMktPopup(row);
  };

  const handleMktSave = async () => {
    if (!mktForm.alert_name.trim()) { alert('알림명을 입력하세요.'); return; }
    if (!mktForm.index_code)        { alert('대상을 선택하세요.'); return; }
    const payload = {
      alert_name:      mktForm.alert_name.trim(),
      index_code:      mktForm.index_code,
      condition:       mktForm.condition,
      threshold_upper: mktForm.condition !== 'LTE' && mktForm.threshold_upper !== '' ? parseFloat(mktForm.threshold_upper) : null,
      threshold_lower: mktForm.condition !== 'GTE' && mktForm.threshold_lower !== '' ? parseFloat(mktForm.threshold_lower) : null,
      cooldown_type:   mktForm.cooldown_type,
      reset_hour:      mktForm.cooldown_type === 'DAILY_TIME' ? parseInt(mktForm.reset_hour)   || 9 : null,
      reset_minute:    mktForm.cooldown_type === 'DAILY_TIME' ? parseInt(mktForm.reset_minute) || 0 : null,
      is_active:       mktForm.is_active,
    };
    setMktBusy(true);
    try {
      if (mktForm.id) await updateAlertConfig(mktForm.id, payload);
      else            await createAlertConfig(payload);
      setMktPopup(null);
      await loadConfigs();
      showMsg('저장되었습니다.');
    } catch (e) {
      const detail = e.response?.data?.detail;
      const msg = Array.isArray(detail)
        ? detail.map(d => d.msg || JSON.stringify(d)).join(', ')
        : (typeof detail === 'string' ? detail : '저장 실패');
      alert(msg);
    }
    finally { setMktBusy(false); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('이 알림 설정을 삭제하시겠습니까?')) return;
    try {
      await deleteAlertConfig(id);
      await loadConfigs();
      showMsg('삭제되었습니다.');
    } catch { showMsg('삭제 실패', 'error'); }
  };

  const handleToggle = async (id) => {
    try {
      await toggleAlertConfig(id);
      await loadConfigs();
    } catch { showMsg('상태 변경 실패', 'error'); }
  };

  // ── content ──────────────────────────────────────────────
  const content = (
    <div className={`ap-wrap${isModal ? ' ap-modal-content' : ''}`}>

      {/* ── 텔레그램 설정 박스 ── */}
      <div className="ap-tg-box">
        <div className="ap-tg-title">텔레그램 알림 설정</div>
        <div className="ap-tg-row">
          <label className="ap-tg-label">알림 사용</label>
          <input type="checkbox" checked={tgSettings.telegram_enabled}
            onChange={e => setTgSettings(p => ({ ...p, telegram_enabled: e.target.checked }))} />
        </div>
        <div className="ap-tg-row">
          <label className="ap-tg-label">Chat ID</label>
          <input
            className="ap-tg-input"
            value={tgSettings.telegram_chat_id}
            onChange={e => setTgSettings(p => ({ ...p, telegram_chat_id: e.target.value }))}
            placeholder="Chat ID 입력"
          />
          <button className="btn btn-sm btn-outline" onClick={handleTest} disabled={testBusy}>
            {testBusy ? '전송중...' : '연결 테스트'}
          </button>
        </div>
        <div className="ap-tg-hint">
          <span className="ap-tg-pin">📌</span>
          <span>Chat ID 확인 방법: 텔레그램에서 봇 검색 → /start 입력 →&nbsp;</span>
          <button className="ap-link-btn" onClick={handleChatIdUrl}>Chat ID 확인하기 (새 탭)</button>
        </div>
        {tgMsg && <div className={`ap-tg-msg ${tgMsg.startsWith('✅') ? 'ok' : 'err'}`}>{tgMsg}</div>}
        <div className="ap-tg-actions">
          <button className="btn btn-sm btn-primary" onClick={handleTgSave} disabled={tgSaving}>
            {tgSaving ? '저장중...' : '저장'}
          </button>
        </div>
      </div>

      {/* ── 수동 알림체크 ── */}
      <div className="ap-check-row">
        <button
          className="btn btn-sm btn-outline ap-check-btn"
          onClick={handleCheckNow}
          disabled={checkBusy}
        >
          {checkBusy ? '체크 중...' : '🔔 알림 즉시 체크'}
        </button>
        <span className="ap-check-hint">시세수집 없이 알림 조건만 즉시 점검합니다.</span>
      </div>

      {/* ── 탭 ── */}
      <div className="ap-tabs">
        {[['type','알림 유형 관리'],['market','시장지수/환율 알림'],['history','발송 이력']].map(([k,lbl]) => (
          <button key={k} className={`ap-tab${tab===k?' ap-tab-active':''}`} onClick={() => setTab(k)}>{lbl}</button>
        ))}
      </div>

      {/* ── 메시지 ── */}
      {message.text && <div className={`ap-msg ap-msg-${message.type}`}>{message.text}</div>}

      {/* ── Tab 1: 알림 유형 관리 ── */}
      {tab === 'type' && (
        <div className="ap-tab-body">
          <AlertTypePage isEmbedded />
        </div>
      )}

      {/* ── Tab 2: 시장지수/환율 알림 ── */}
      {tab === 'market' && (
        <div className="ap-tab-body">
          <div className="ap-tab-toolbar">
            <button className="btn btn-sm btn-primary" onClick={openMktNew}>+ 알림 추가</button>
          </div>
          <div className="ap-grid-wrap">
            <table className="grid-table ap-grid">
              <thead>
                <tr>
                  <th>알림명</th>
                  <th>지수/환율</th>
                  <th className="col-center">조건</th>
                  <th className="col-right">기준값(상/하)</th>
                  <th className="col-center">재발송</th>
                  <th className="col-center">활성</th>
                  <th className="col-center">수정</th>
                  <th className="col-center">삭제</th>
                </tr>
              </thead>
              <tbody>
                {loading && <tr><td colSpan={8} className="grid-empty">조회중...</td></tr>}
                {!loading && configs.length === 0 && (
                  <tr><td colSpan={8} className="grid-empty">등록된 시장지수/환율 알림이 없습니다.</td></tr>
                )}
                {configs.map(row => (
                  <tr key={row.id}>
                    <td>{row.alert_name}</td>
                    <td>{ALL_INDEX_ITEMS.find(i => i.value === row.index_code)?.label || row.index_code || '-'}</td>
                    <td className="col-center">{condLabel(row.condition)}</td>
                    <td className="col-right ap-threshold-cell">
                      {row.threshold_upper != null && <span className="ap-thresh-up">↑{row.threshold_upper?.toLocaleString()}</span>}
                      {row.threshold_lower != null && <span className="ap-thresh-dn">↓{row.threshold_lower?.toLocaleString()}</span>}
                    </td>
                    <td className="col-center">{cdLabel(row.cooldown_type, row.reset_hour, row.reset_minute)}</td>
                    <td className="col-center">
                      <label className="ap-toggle">
                        <input type="checkbox" checked={row.is_active} onChange={() => handleToggle(row.id)} />
                        <span className="ap-toggle-slider" />
                      </label>
                    </td>
                    <td className="col-center">
                      <button className="btn btn-xs btn-outline" onClick={() => openMktEdit(row)}>수정</button>
                    </td>
                    <td className="col-center">
                      <button className="btn btn-xs btn-danger" onClick={() => handleDelete(row.id)}>삭제</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* ── Tab 3: 발송 이력 ── */}
      {tab === 'history' && (
        <div className="ap-tab-body">
          <div className="ap-grid-wrap">
            <table className="grid-table ap-grid ap-hist-grid">
              <colgroup>
                <col style={{ width: 130 }} />
                <col style={{ width: 70 }} />
                <col style={{ width: 100 }} />
                <col style={{ width: 80 }} />
                <col style={{ width: 50 }} />
                <col style={{ width: 70 }} />
                <col style={{ width: 70 }} />
                <col style={{ width: 60 }} />
                <col />
              </colgroup>
              <thead>
                <tr>
                  <th className="col-center">발송시각</th>
                  <th className="col-center">출처</th>
                  <th>종목/지수</th>
                  <th>계좌</th>
                  <th className="col-center">차수</th>
                  <th className="col-right">실제값</th>
                  <th className="col-right">기준값</th>
                  <th className="col-center">결과</th>
                  <th>메시지</th>
                </tr>
              </thead>
              <tbody>
                {histLoading && <tr><td colSpan={9} className="grid-empty">조회중...</td></tr>}
                {!histLoading && history.data.length === 0 && (
                  <tr><td colSpan={9} className="grid-empty">발송 이력이 없습니다.</td></tr>
                )}
                {history.data.map(row => (
                  <tr key={row.id}>
                    <td className="col-center">{fDt(row.sent_at)}</td>
                    <td className="col-center">{SOURCE_LABELS[row.alert_source] || row.alert_source || '-'}</td>
                    <td>{row.ticker || row.index_code || '-'}</td>
                    <td>{row.account_name || '-'}</td>
                    <td className="col-center">{row.turn_no ? `${row.turn_no}차` : '-'}</td>
                    <td className="col-right">{fVal(row.actual_value, row.alert_metric)}</td>
                    <td className="col-right">{fVal(row.threshold, row.alert_metric)}</td>
                    <td className="col-center">
                      {row.is_success
                        ? <span className="ap-hist-ok">✅ 성공</span>
                        : <span className="ap-hist-fail-badge">❌ 실패</span>
                      }
                    </td>
                    <td
                      className="ap-hist-msg-cell"
                      title={row.message || ''}
                      onClick={() => row.message && setMsgDetail(row.message)}
                    >
                      {row.message || '-'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {history.total > 20 && (
            <div className="ap-paging">
              {Array.from({ length: Math.ceil(history.total / 20) }, (_, i) => i + 1).map(p => (
                <button key={p} className={`ap-page-btn${p === history.page ? ' active' : ''}`}
                  onClick={() => loadHistory(p)}>{p}</button>
              ))}
            </div>
          )}
        </div>
      )}

      {/* ── 시장지수 알림 팝업 ── */}
      {mktPopup !== null && (
        <div className="ap-overlay">
          <div className="ap-popup">
            <div className="ap-popup-hd">
              {mktForm.id ? '시장지수/환율 알림 수정' : '시장지수/환율 알림 등록'}
              <button className="ap-popup-close" onClick={() => setMktPopup(null)}>✕</button>
            </div>
            <div className="ap-popup-bd">
              <div className="ap-form-row">
                <label>알림명</label>
                <input className="ap-input" value={mktForm.alert_name}
                  onChange={e => setMktForm(p => ({ ...p, alert_name: e.target.value }))} />
              </div>
              <div className="ap-form-row">
                <label>대상</label>
                <select className="ap-select" value={mktForm.index_code}
                  onChange={e => setMktForm(p => ({ ...p, index_code: e.target.value }))}>
                  {INDEX_OPTIONS.map(g => (
                    <optgroup key={g.group} label={`-- ${g.group} --`}>
                      {g.items.map(i => <option key={i.value} value={i.value}>{i.label}</option>)}
                    </optgroup>
                  ))}
                </select>
              </div>
              <div className="ap-form-row">
                <label>조건</label>
                <div className="ap-radio-group">
                  {[['GTE','이상(>=)'],['LTE','이하(<=)'],['BOTH','양방향']].map(([v,lbl]) => (
                    <label key={v} className="ap-radio">
                      <input type="radio" checked={mktForm.condition === v}
                        onChange={() => setMktForm(p => ({ ...p, condition: v }))} />
                      {lbl}
                    </label>
                  ))}
                </div>
              </div>
              {mktForm.condition !== 'LTE' && (
                <div className="ap-form-row">
                  <label>이상 기준값</label>
                  <input className="ap-input ap-input-sm" type="number" step="any" value={mktForm.threshold_upper}
                    onChange={e => setMktForm(p => ({ ...p, threshold_upper: e.target.value }))} />
                </div>
              )}
              {mktForm.condition !== 'GTE' && (
                <div className="ap-form-row">
                  <label>이하 기준값</label>
                  <input className="ap-input ap-input-sm" type="number" step="any" value={mktForm.threshold_lower}
                    onChange={e => setMktForm(p => ({ ...p, threshold_lower: e.target.value }))} />
                </div>
              )}
              <div className="ap-form-row">
                <label>재발송 방식</label>
                <select className="ap-select" value={mktForm.cooldown_type}
                  onChange={e => setMktForm(p => ({ ...p, cooldown_type: e.target.value }))}>
                  {COOLDOWN_OPTIONS.map(o => (
                    <option key={o.value} value={o.value}>{o.label}</option>
                  ))}
                </select>
              </div>
              {mktForm.cooldown_type === 'DAILY_TIME' && (
                <div className="ap-form-row">
                  <label>재발송 가능 시각</label>
                  <input className="ap-input ap-input-xs" type="number" min={0} max={23}
                    value={mktForm.reset_hour}
                    onChange={e => setMktForm(p => ({ ...p, reset_hour: e.target.value }))} />
                  <span className="ap-unit">시</span>
                  <select className="ap-select ap-select-xs"
                    value={mktForm.reset_minute}
                    onChange={e => setMktForm(p => ({ ...p, reset_minute: parseInt(e.target.value) }))}>
                    {MINUTE_OPTIONS.map(m => (
                      <option key={m} value={m}>{String(m).padStart(2, '0')}</option>
                    ))}
                  </select>
                  <span className="ap-unit">분 (다음날 이 시각 이후 재발송)</span>
                </div>
              )}
              <div className="ap-form-row">
                <label>활성 여부</label>
                <label className="ap-radio">
                  <input type="checkbox" checked={mktForm.is_active}
                    onChange={e => setMktForm(p => ({ ...p, is_active: e.target.checked }))} />
                  활성
                </label>
              </div>
            </div>
            <div className="ap-popup-ft">
              <button className="btn btn-sm btn-outline" onClick={() => setMktPopup(null)}>취소</button>
              <button className="btn btn-sm btn-primary" onClick={handleMktSave} disabled={mktBusy}>
                {mktBusy ? '저장중...' : '저장'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── 발송 메시지 팝업 ── */}
      {msgDetail && (
        <div className="ap-overlay" onClick={() => setMsgDetail(null)}>
          <div className="ap-popup ap-msg-popup" onClick={e => e.stopPropagation()}>
            <div className="ap-popup-hd">
              발송 메시지
              <button className="ap-popup-close" onClick={() => setMsgDetail(null)}>✕</button>
            </div>
            <div className="ap-popup-bd" style={{ whiteSpace: 'pre-wrap', fontSize: 13 }}>
              {msgDetail}
            </div>
            <div className="ap-popup-ft">
              <button className="btn btn-sm btn-outline" onClick={() => setMsgDetail(null)}>닫기</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );

  if (isModal) {
    return (
      <div className="ap-modal-overlay">
        <div className="ap-modal-box">
          <div className="ap-modal-hd">
            알림 설정
            <button className="ap-modal-close" onClick={onClose}>✕</button>
          </div>
          {content}
        </div>
      </div>
    );
  }

  return (
    <PageLayout title="알림 설정">
      {content}
    </PageLayout>
  );
};

export default AlertPage;
